
import { useEffect, useMemo, useState } from 'react'
import { Brush, CartesianGrid, ComposedChart, Legend, Line, LineChart, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis } from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';
import { DESDataModel } from '../services/DESDataModel';
import DeviceStore from '../../../Store/DeviceStorage';
import { usePapaParse } from 'react-papaparse';
import './Graphs.css'
import { AiAnalizeData, DeviceData } from '../../../API';
import { getAiDeviceDataItems, getDeviceDataItems } from '../../../Service/Devices/DevicesService';
import MeteorologicalData from '../MeteorologicalData';
import { downloadData } from 'aws-amplify/storage';

type MergeData = {
    timestamp: number;
    dyMm: number;
    count: number;
    category: string;
    dateTimeOriginal: string
}

function Graphs() {

    const { readString } = usePapaParse();

    const currendDevice = DeviceStore((state) => state.currentDevice)

    const [cameraRaw, setCameraRaw] = useState<DESDataModel[]>([])
    const [camera, setCamera] = useState<DeviceData[]>([])
    const [mergeData, setMergeData] = useState<MergeData[]>([])

    const { RangePicker } = DatePicker;
    const [dateFromPickerSetValue, setDateFromPickerSetValue] = useState<number>(-1);
    const [dateToPickerSetValue, setDateToPickerSetValue] = useState<number>(-1);
    const [rangePresetsValue, setRangePresetsValue] = useState<number>(0);

    useEffect(() => {
        if (currendDevice) {
            if (currendDevice.urlData !== null) {
                getData()
            } else {
                if (!currendDevice.cameraAI) {
                    const dataFormatString = currendDevice.dateFormat
                    setRangePresetsValue(currendDevice.rangePreset)
                    getDeviceDataItems(currendDevice.id, dateFromPickerSetValue, dateToPickerSetValue)
                        .then((dataArray: DeviceData[]) => {
                            const data = (dataArray as DeviceData[]).map(x => {
                                let dmYInt = 0
                                if (x.dyMm !== undefined)
                                    dmYInt = (Math.round((Number(x.dyMm) + Number.EPSILON) * 10000) / 10000) * -1;
                                if (x.dMy !== undefined)
                                    dmYInt = (Math.round((Number(x.dMy) + Number.EPSILON) * 10000) / 10000) * -1;
                                x.dyMm = dmYInt;
                                x.DateTimeOriginal = dayjs.unix(Number(x.timestamp)).format(dataFormatString)
                                return x;
                            })
                            const kamera = data.filter((data: DeviceData) => Number(data.dyMm) <= currendDevice.maxValue && Number(data.dyMm) >= currendDevice.minValue)
                            setCamera(data)
                        })
                }
            }
        }
    }, [currendDevice, dateFromPickerSetValue, dateToPickerSetValue])

    useEffect(() => {
        if (currendDevice && currendDevice.cameraAI)
            getAiDeviceDataItems(currendDevice.id, dateFromPickerSetValue, dateToPickerSetValue).then((data) => {
                if (data.length) {
                    const dataFormatString = currendDevice.dateFormat

                    const aiElements = (data as AiAnalizeData[]).map(x => {
                        let dmYInt = 0
                        if (x.dyMm !== undefined)
                            dmYInt = (Math.round((Number(x.dyMm) + Number.EPSILON) * 10000) / 10000) * -1;
                        x.dyMm = dmYInt;
                        return x;
                    })
                    if (aiElements.length) {
                        let imetArray = []

                        aiElements.forEach((item) => {
                            let countWeightMin = 0;
                            let countWeightMax = 0;
                            if (item.category === 'car') {
                                countWeightMin += 1;
                                countWeightMax += 3.5;
                            }

                            if (item.category === 'van') {
                                countWeightMin += 2;
                                countWeightMax += 3.5;
                            }

                            if (item.category === 'light') {
                                countWeightMin += 2.5;
                                countWeightMax += 3.5;
                            }
                            if (item.category === 'bus') {
                                countWeightMin += 2;
                                countWeightMax += 3.5;
                            }
                            if (item.category === 'caravan') {
                                countWeightMin += 2;
                                countWeightMax += 3.5;
                            }
                            if (item.category === 'heavy') {
                                countWeightMin += 15;
                                countWeightMax += 25;
                            }
                            if (item.category === 'truck trailer') {
                                countWeightMin += 20;
                                countWeightMax += 42;
                            }
                            if (item.category === 'tractor') {
                                countWeightMin += 20;
                                countWeightMax += 42;
                            }
                            const merge: MergeData = {
                                dyMm: Number(item.dyMm),
                                timestamp: Number(item.timestamp),
                                dateTimeOriginal: dayjs.unix(Number(item.timestamp)).format(dataFormatString),
                                count: item.count,
                                category: 'min: ' + countWeightMin + ' max: ' + countWeightMax
                            };
                            imetArray.push(merge)
                        })
                        setMergeData(imetArray)
                    }
                }
            })
    }, [currendDevice, dateFromPickerSetValue, dateToPickerSetValue])

    useEffect(() => {
        if (rangePresetsValue > -1) {
            setDateFromPickerSetValue(rangePresets[rangePresetsValue].value[0].startOf('day').unix())
            setDateToPickerSetValue(dayjs().endOf('day').unix())
        }
    }, [rangePresetsValue]);


    async function getData() {

        try {
            if (currendDevice === null) return
            setRangePresetsValue(currendDevice.rangePreset)
            let dataFormatString = currendDevice.dateFormat

            const downloadResult = await downloadData({
                key: currendDevice.urlData
            }).result;
            const text = await downloadResult.body.text();
            readString(text, {
                header: true,
                worker: true,
                complete: (results) => {
                    const data = (results.data as DESDataModel[]).map(x => {
                        let dmYInt = 0
                        if (x.dyMm !== undefined)
                            dmYInt = (Math.round((Number(x.dyMm) + Number.EPSILON) * 10000) / 10000) * -1;
                        x.dyMm = dmYInt;
                        x.DateTimeOriginalTimeStamp = dayjs(x.DateTimeOriginal, dataFormatString).unix()
                        return x;
                    })
                    setCameraRaw(data.filter((x) => x.Time !== ''))
                },
            });
        } catch (error) {
            console.log('Error : ', error);
        }
    }

    // Effect to process cameraRaw (from CSV) into camera state (DeviceData[])
    useEffect(() => {
        // Make sure currendDevice is available before proceeding
        if (!currendDevice) {
            setCamera([]); // Clear camera data if no device selected
            return;
        }

        // 1. Filter cameraRaw by the selected date range
        let filteredByDate = cameraRaw.filter((data: DESDataModel) => (
            data.DateTimeOriginalTimeStamp >= dateFromPickerSetValue &&
            data.DateTimeOriginalTimeStamp <= dateToPickerSetValue
        ));

        // 2. Filter by device's min/max value constraints
        let filteredByValue = filteredByDate.filter((data: DESDataModel) =>
            data.dyMm <= currendDevice.maxValue && data.dyMm >= currendDevice.minValue
            // Consider adding dxMm filter if needed and uncommented:
            // && data.dxMm <= currendDevice.maxValueX && data.dxMm >= currendDevice.minValueX
        );

        // 3. Map the filtered DESDataModel[] to DeviceData[] structure needed for charts
        const mappedData: DeviceData[] = filteredByDate.map((item: DESDataModel): DeviceData => {
            // Construct the DeviceData object
            // Ensure all required fields of DeviceData are present, even if with default/null values
            return {
                // --- Fields directly from DESDataModel ---
                timestamp: item.DateTimeOriginalTimeStamp.toString(), // Use the pre-calculated timestamp
                dyMm: item.dyMm, // Use the pre-processed dyMm
                dxMm: item.dxMm, // Use dxMm directly (assuming it exists)
                DateTimeOriginal: item.DateTimeOriginal, // Use the original string for display
                id: '', // Placeholder - adjust if a real ID is available/needed
                deviceID: currendDevice.id,
                __typename: 'DeviceData',
                createdAt: '',
                updatedAt: ''
            };
        });
        console.log(filteredByDate)
        // 4. Set the state with the mapped data
        setCamera(mappedData);

    }, [cameraRaw, dateFromPickerSetValue, dateToPickerSetValue, currendDevice]); // Added currendDevice dependency


    const rangePresets: {
        label: string;
        value: [Dayjs, Dayjs];
    }[] = [
            { label: 'Ostatnie 7 dni', value: [dayjs().add(-7, 'd'), dayjs()] },
            { label: 'Ostatnie 30 dni', value: [dayjs().add(-30, 'd'), dayjs()] },
            { label: 'Ostatnie 60 dni', value: [dayjs().add(-60, 'd'), dayjs()] },
            { label: 'Cały zakres', value: [dayjs('2022-01-01'), dayjs()] },
        ];

    const onRangeChange = (dates: null | (Dayjs | null)[]) => {
        if (dates) {
            const from = dates[0]?.startOf('day').unix();
            const to = dates[1]?.endOf('day').unix();
            if (from && to) {
                setDateFromPickerSetValue(from);
                setDateToPickerSetValue(to);
            }
        } else {
            console.log('Clear');
        }
    };

    // Use useMemo to calculate axis domains based on camera data
    const verticalDomain = useMemo(() => {
        if (camera.length === 0) return [-1, 1]; // Default domain
        let min = Infinity;
        let max = -Infinity;
        if (camera.length > 0) {
            camera.forEach((data: DeviceData) => {
                const val = Number(data.dyMm);
                if (val < min) min = val;
                if (val > max) max = val;
            });
        }
        // Add padding to the domain
        return [Math.floor(min) - 1, Math.ceil(max) + 1];
    }, [camera]);

    const horizontalDomain = useMemo(() => {
        if (camera.length === 0) return [-1, 1]; // Default domain
        let min = Infinity;
        let max = -Infinity;
        if (camera.length > 0) {
            camera.forEach((data: DeviceData) => {
                const val = Number(data.dxMm); // Assuming dxMm might be used
                if (val < min) min = val;
                if (val > max) max = val;
            });
        }
        // Add padding to the domain
        return [Math.floor(min) - 1, Math.ceil(max) + 1];
    }, [camera]);

    return (
        <div className="GraphsContainer"><div>
            {rangePresetsValue > -1 &&
                <RangePicker onChange={onRangeChange} presets={rangePresets} defaultValue={[rangePresets[rangePresetsValue].value[0].startOf('day'), dayjs()]} />
            }
        </div>
            {currendDevice && <div>
                <h1>{currendDevice.name}</h1>
                {currendDevice.dyMm &&
                    <>
                        <h2>Kamera przemieszczenie pionowe</h2>
                        <ResponsiveContainer width="95%" height={350}>
                            <LineChart
                                data={camera}
                                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                            >
                                <XAxis dataKey="DateTimeOriginal" />
                                <YAxis type="number" domain={verticalDomain} allowDataOverflow={true} />
                                <Tooltip />
                                <CartesianGrid stroke="#f5f5f5" />
                                <Line type="monotone" dataKey="dyMm" stroke="#000dff" yAxisId={0} />
                                <Brush />
                            </LineChart>
                        </ResponsiveContainer>
                    </>
                }
                {currendDevice.dxMm &&
                    <>
                        <h2>Kamera przemieszczenie poziome</h2>
                        <ResponsiveContainer width="95%" height={350}>
                            <LineChart
                                data={camera}
                                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                            >
                                <XAxis dataKey="DateTimeOriginal" />
                                <YAxis type="number" domain={horizontalDomain} allowDataOverflow={true} />
                                <Tooltip />
                                <CartesianGrid stroke="#f5f5f5" />
                                <Line type="monotone" dataKey="dxMm" stroke="#ff0000" yAxisId={0} />
                                <Brush />
                            </LineChart>
                        </ResponsiveContainer>
                    </>
                }
                {currendDevice.cameraAI &&
                    <>
                        <h2>Kamera przemieszczenie poziome</h2>
                        <ResponsiveContainer width="95%" height={350}>
                            <ComposedChart data={mergeData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }} >
                                <XAxis dataKey="dateTimeOriginal" />
                                <YAxis type="number" domain={['auto', 'auto']} allowDataOverflow={true} />

                                <Tooltip />
                                <CartesianGrid stroke="#f5f5f5" />
                                <Line type="monotone" dataKey="dyMm" name="ugięcie" stroke="#000dff" dot={null} />
                                <Scatter dataKey='category' name="zakres" fill="#8884d8" />
                                <Scatter dataKey='count' name="liczba" fill="#8884d8" />
                                <Brush />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </>
                }
                {currendDevice.weatherStation &&
                    <MeteorologicalData />
                }
            </div>
            }
        </div>
    )
}

export default Graphs